import React, { createContext, useState, useContext, useEffect } from 'react';
import { format, formatDistanceToNow } from 'date-fns/esm';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';

const NotificationContext = createContext();

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Fetch notifications from Firestore
    const fetchNotifications = async () => {
      try {
        // Get user's notifications with options
        const options = {
          limit: 50, // Limit to 50 most recent notifications
          orderBy: 'createdAt', // Order by creation time
          orderDirection: 'desc' // Most recent first
        };
        
        const userNotifications = await notificationService.getUserNotifications(currentUser.uid, options);
        
        // Transform the data to match our expected format
        const formattedNotifications = userNotifications.map(notification => ({
          id: notification.id,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          timestamp: notification.createdAt?.toDate().toISOString() || new Date().toISOString(),
          read: notification.isRead,
          data: {
            // Extract relevant data based on notification type
            ...(notification.campaignId && { campaignId: notification.campaignId }),
            ...(notification.paymentId && { paymentId: notification.paymentId }),
            ...(notification.amount && { amount: notification.amount }),
            ...(notification.status && { status: notification.status }),
            ...(notification.referralId && { referralId: notification.referralId }),
            ...(notification.conversationId && { conversationId: notification.conversationId }),
            ...(notification.senderId && { senderId: notification.senderId }),
            ...(notification.submissionId && { submissionId: notification.submissionId }),
            // Add relatedId and relatedType which are used in many notification types
            ...(notification.relatedId && { relatedId: notification.relatedId }),
            ...(notification.relatedType && { relatedType: notification.relatedType })
          }
        }));
        
        setNotifications(formattedNotifications);
        
        // Get unread count
        const unreadCount = await notificationService.getUnreadCount(currentUser.uid);
        setUnreadCount(unreadCount);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNotifications();
    
    // Set up a refresh interval to check for new notifications every minute
    const intervalId = setInterval(fetchNotifications, 60000);
    
    return () => clearInterval(intervalId);
  }, [currentUser]);

  const markAsRead = async (notificationId) => {
    try {
      // Update in Firestore
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      
      // Update unread count
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUser) return;
    
    try {
      // Update in Firestore
      await notificationService.markAllAsRead(currentUser.uid);
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notif => ({ ...notif, read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const notifToDelete = notifications.find(n => n.id === notificationId);
      const wasUnread = notifToDelete && !notifToDelete.read;
      
      // Delete from Firestore
      await notificationService.deleteNotification(notificationId);
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.filter(notif => notif.id !== notificationId)
      );
      
      // Update unread count if the deleted notification was unread
      if (wasUnread) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const clearAllNotifications = async () => {
    if (!currentUser) return;
    
    try {
      // Clear all notifications in Firestore
      await notificationService.clearAllNotifications(currentUser.uid);
      
      // Update local state
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  // Function to add a new notification (for testing or manual creation)
  const addNotification = async (notification) => {
    if (!currentUser) return;
    
    try {
      // Create notification in Firestore
      const { type, title, message, data = {} } = notification;
      
      await notificationService.createNotification(
        currentUser.uid,
        type,
        title,
        message,
        data
      );
      
      // Refresh notifications to include the new one
      const options = { limit: 50, orderBy: 'createdAt', orderDirection: 'desc' };
      const userNotifications = await notificationService.getUserNotifications(currentUser.uid, options);
      
      // Transform and update local state
      const formattedNotifications = userNotifications.map(notif => ({
        id: notif.id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        timestamp: notif.createdAt?.toDate().toISOString() || new Date().toISOString(),
        read: notif.isRead,
        data: {
          // Extract all relevant fields
          ...(notif.campaignId && { campaignId: notif.campaignId }),
          ...(notif.paymentId && { paymentId: notif.paymentId }),
          ...(notif.amount && { amount: notif.amount }),
          ...(notif.status && { status: notif.status }),
          ...(notif.referralId && { referralId: notif.referralId }),
          ...(notif.conversationId && { conversationId: notif.conversationId }),
          ...(notif.senderId && { senderId: notif.senderId }),
          ...(notif.submissionId && { submissionId: notif.submissionId }),
          ...(notif.relatedId && { relatedId: notif.relatedId }),
          ...(notif.relatedType && { relatedType: notif.relatedType })
        }
      }));
      
      setNotifications(formattedNotifications);
      setUnreadCount(await notificationService.getUnreadCount(currentUser.uid));
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    addNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
